import { useState } from 'react';
import { Home, Plus, X } from 'lucide-react';
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@roger/ui-components';

export interface CodeOption {
  code: string;
  description: string;
  homeHealth?: boolean;
}

/** Single code picker: searchable combobox over a reference list (NOAForm pattern). */
export function CodeCombobox({
  value,
  options,
  placeholder = 'Select code',
  onChange,
}: {
  value: string;
  options: CodeOption[];
  placeholder?: string;
  onChange: (code: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant='outline'
          size='sm'
          className='w-20 justify-between overflow-hidden font-mono'
        >
          <span className='min-w-0 flex-1 truncate text-left'>
            {value || placeholder}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-80 p-0' align='start'>
        <Command>
          <CommandInput
            placeholder='Search code or description…'
            className='text-xs'
          />
          <CommandList className='max-h-60'>
            <CommandEmpty className='text-xs py-2 text-center text-muted-foreground'>
              No codes found.
            </CommandEmpty>
            <CommandGroup>
              {options.map(option => (
                <CommandItem
                  key={option.code}
                  value={`${option.code} ${option.description}`}
                  onSelect={() => {
                    onChange(option.code);
                    setOpen(false);
                  }}
                  className='text-xs'
                >
                  <span className='font-mono font-semibold w-8 shrink-0'>
                    {option.code}
                  </span>
                  <span className='flex-1 ml-2 truncate'>
                    {option.description}
                  </span>
                  {option.homeHealth && (
                    <span title='Common in home health'>
                      <Home className='h-3 w-3 ml-1 shrink-0 text-teal-600' />
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Editable list of single codes (UB-04 condition codes): combobox rows + add/remove. */
export function CodeListField({
  label,
  options,
  values,
  onChange,
  showLabel = true,
}: {
  label: string;
  options: CodeOption[];
  values: string[];
  onChange: (next: string[]) => void;
  showLabel?: boolean;
}) {
  return (
    <div className='flex flex-col gap-2'>
      {showLabel && <span className='text-sm font-medium'>{label}</span>}
      {values.map((code, index) => (
        <div key={index} className='flex items-center gap-2'>
          <CodeCombobox
            value={code}
            options={options}
            onChange={next => {
              const copy = [...values];
              copy[index] = next;
              onChange(copy);
            }}
          />
          <Button
            type='button'
            variant='ghost'
            size='icon'
            aria-label={`Remove ${label}`}
            onClick={() => onChange(values.filter((_, j) => j !== index))}
          >
            <X className='h-4 w-4' />
          </Button>
        </div>
      ))}
      <Button
        type='button'
        variant='outline'
        size='sm'
        className='self-start'
        onClick={() => onChange([...values, ''])}
      >
        <Plus className='h-3 w-3 mr-1' />
        Add {label}
      </Button>
    </div>
  );
}
